import { API_ENDPOINT } from "@/constants/appConstants";
import { getHttpMethod } from "@/helpers/getHttpMethod";
import {
  GET_AUTO_APPLY_QUERY_KEY,
  GET_VALIDATE_PROFILE_QUERY_KEY,
} from "@/lib/queryKeys";
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { ActionType, ErrorResponse } from "@/components/profile/types";

import { AutoApplyResponse } from "../../types";

export const autoApplyAPI = async (
  jobId?: string,
): Promise<AutoApplyResponse> => {
  try {
    const host = window.location.host;
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.getAutoApply}?jobId=${jobId}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching data, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const validateProfileAPI = async (): Promise<any> => {
  try {
    const host = window.location.host;
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.validateProfile}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    const data: any = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching data, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const useValidateProfile = (
  isFetchingEnabled = false,
): UseQueryResult<any> => {
  return useQuery({
    queryKey: [GET_VALIDATE_PROFILE_QUERY_KEY],
    queryFn: () => validateProfileAPI(),
    enabled: isFetchingEnabled,
  });
};

export const useAutoApply = (
  jobId: string,
  isFetchingEnabled = false,
): UseQueryResult<AutoApplyResponse> => {
  return useQuery({
    queryKey: [GET_AUTO_APPLY_QUERY_KEY, jobId],
    queryFn: () => autoApplyAPI(jobId),
    enabled: isFetchingEnabled,
  });
};

export const useUpdateAutoApply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data, action }: { data: any; action: ActionType }) => {
      const method = getHttpMethod(action);
      const response = await fetch(
        API_ENDPOINT(window.location.host).middleware.updateAutoApply,
        {
          method,
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse;
        throw new Error(
          errorData.error.message.errorMessage || "Something went wrong",
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_AUTO_APPLY_QUERY_KEY] });
    },
  });
};
